/**
 * Helpers centralizados de formatação de data/hora em fuso Brasil.
 *
 * Todas as datas exibidas no app (leads, agendamentos, faturas, auditoria,
 * notificações, etc) devem passar por estes helpers pra garantir que fiquem
 * em America/Sao_Paulo (BRT, UTC-3) — independente do fuso do navegador do
 * usuário.
 *
 * Motivação: o bug visível no print dos leads mostrou que `new Date(iso)
 * .toLocaleString('pt-BR')` depende do TZ DO NAVEGADOR — se o usuário estiver
 * viajando, ou se o telefone tiver TZ configurado errado, a data aparece
 * deslocada. Forçar `timeZone: 'America/Sao_Paulo'` resolve isso.
 *
 * Uso:
 *   import { formatDateTime, formatDate, formatTime, formatRelative } from '@/utils/formatDateTime';
 *
 *   formatDateTime('2026-04-11T20:10:57Z')  // "11/04/2026, 17:10"
 *   formatDate('2026-04-11T20:10:57Z')      // "11/04/2026"
 *   formatTime('2026-04-11T20:10:57Z')      // "17:10"
 *   formatRelative('2026-04-11T20:10:57Z')  // "agora" / "5min" / "2h" / "3d"
 */

const BR_TZ = 'America/Sao_Paulo';
const BR_LOCALE = 'pt-BR';

/**
 * Converte qualquer input (ISO string, Date, timestamp ms) pra Date. Retorna
 * null se a entrada for falsy ou invalida.
 */
function toDate(input) {
  if (!input && input !== 0) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * "11/04/2026, 17:10" — data + hora curtas, fuso Brasil.
 * Usado em: auditoria, leads, faturas, histórico de ações.
 */
export function formatDateTime(input, options = {}) {
  const d = toDate(input);
  if (!d) return '—';
  return d.toLocaleString(BR_LOCALE, {
    timeZone: BR_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}

/**
 * "11/04/2026" — apenas data, fuso Brasil.
 * Usado em: cards, listagens curtas.
 */
export function formatDate(input, options = {}) {
  const d = toDate(input);
  if (!d) return '—';
  return d.toLocaleDateString(BR_LOCALE, {
    timeZone: BR_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  });
}

/**
 * "17:10" — apenas hora, fuso Brasil.
 * Usado em: slots de agendamento, timestamps de notificações.
 */
export function formatTime(input, options = {}) {
  const d = toDate(input);
  if (!d) return '—';
  return d.toLocaleTimeString(BR_LOCALE, {
    timeZone: BR_TZ,
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}

/**
 * Formato longo com mes por extenso: "11 de abril de 2026, 17:10".
 * Usado em: emails, relatórios, telas de confirmação.
 */
export function formatDateTimeLong(input) {
  const d = toDate(input);
  if (!d) return '—';
  return d.toLocaleString(BR_LOCALE, {
    timeZone: BR_TZ,
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * "agora" / "5min" / "2h" / "3d" — tempo relativo curto.
 * Usado em: feeds de atividade, notificações do header.
 */
export function formatRelative(input) {
  const d = toDate(input);
  if (!d) return '—';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'agora';
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/**
 * "YYYY-MM-DD" no fuso Brasil — útil pra inputs type="date" e comparações.
 */
export function formatDateISO(input) {
  const d = toDate(input);
  if (!d) return '';
  // Usa Intl pra extrair os componentes no TZ Brasil e remonta em ISO.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BR_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}
